import React, { useState } from "react";
// No react-bootstrap imports — plain HTML elements with inline styles to avoid global CSS overrides
import DvsEvaluationService, {
  EvaluationSection,
  PaperForEvaluation,
} from "../../../_services/DvsEvaluationService";
import "../dvs-evaluation.css";

interface Props {
  paper: PaperForEvaluation;
  onMarksChange: (attemptId: number, field: "marks" | "remarks", value: string) => void;
  onSubmit: () => void;
  onReportIssue: () => void;
  submitting: boolean;
  validationErrors: Record<number, string>;
}

const MarksEntryPanel: React.FC<Props> = ({
  paper,
  onMarksChange,
  onSubmit,
  onReportIssue,
  submitting,
  validationErrors,
}) => {
  const [savingField, setSavingField] = useState<number | null>(null);

  const handleBlur = async (
    attemptId: number,
    type: "marks" | "remarks",
    value: string,
  ) => {
    if (paper.evaluatorType !== 1) return;
    setSavingField(attemptId);
    try {
      await DvsEvaluationService.updateQuestionMarks(attemptId, type, value);
    } catch (err) {
      console.error("Failed to save:", err);
    } finally {
      setSavingField(null);
    }
  };

  return (
    <div className="dvs-eval-root dvs-eval-marks-panel">
      {/* Section title bar */}
      <div className="dvs-eval-marks-panel__title">Marks By Examiner</div>
      {/* Test header */}
      <div className="dvs-eval-marks-panel__test">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: "8px" }}>
          <span className="dvs-eval-marks-panel__test-name">{paper.testName}</span>
          {paper.showStudentName && paper.studentName && (
            <span style={{ fontSize: "0.78em", color: "var(--text-faint)" }}>
              {paper.studentName}
            </span>
          )}
        </div>
        <div className="dvs-eval-marks-panel__test-meta">
          Evaluator Type {paper.evaluatorType}
          {paper.questionPaperUrl && (
            <>
              {" · "}
              <a href={paper.questionPaperUrl} target="_blank" rel="noreferrer">
                Question Paper
              </a>
              {paper.questionPaperPassword && (
                <span style={{ marginLeft: "4px", color: "var(--text-faint)" }}>
                  (pwd: {paper.questionPaperPassword})
                </span>
              )}
            </>
          )}
          {paper.answerSheetUrl && (
            <>
              {" · "}
              <a href={paper.answerSheetUrl} target="_blank" rel="noreferrer">
                Answer Key
              </a>
            </>
          )}
        </div>
      </div>

      {/* Scrollable marks table */}
      <div style={{ flex: 1, overflowY: "auto", padding: "12px" }}>
        {paper.sections.map((section) => (
          <div key={section.sectionId} style={{ marginBottom: "16px" }}>
            <div className="dvs-eval-marks-panel__section-header" style={{ marginBottom: "6px" }}>
              {section.sectionName}
            </div>

            {section.showTotalMarksOnly ? (
              /* Show total marks summary only */
              <div style={{
                background: "var(--surface-page)",
                border: "1px solid var(--border-default)",
                borderRadius: "6px",
                padding: "14px 16px",
                fontSize: "0.9em",
              }}>
                <div style={{ color: "var(--text-muted)", marginBottom: "4px", fontSize: "0.85em" }}>
                  Total Marks Obtained
                </div>
                <div style={{ fontSize: "1.3em", fontWeight: 700, color: "var(--brand-primary)" }}>
                  {section.totalMarks ?? 0} / {section.maxMarks ?? 0}
                </div>
              </div>
            ) : (
              /* Show full question table */
              <table
                style={{
                  width: "100%",
                  fontSize: "0.82em",
                  borderCollapse: "collapse",
                }}
              >
                <thead>
                  <tr style={{ background: "var(--surface-sunken)" }}>
                    <th style={thStyle}>Q#</th>
                    <th style={thStyle}>Max</th>
                    <th style={thStyle}>Obtained</th>
                    <th style={thStyle}>Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {section.questions.map((q) => (
                    <tr key={q.attemptId}>
                      <td style={tdStyle}>{q.questionNumber}</td>
                      <td style={tdStyle}>{q.maxMarks}</td>
                      <td style={tdStyle}>
                        <input
                          type="number"
                          step="0.5"
                          min="0"
                          max={q.maxMarks}
                          value={q.obtainedMarks ?? ""}
                          onChange={(e) =>
                            onMarksChange(q.attemptId, "marks", e.target.value)
                          }
                          onBlur={(e) =>
                            handleBlur(q.attemptId, "marks", e.target.value)
                          }
                          className={`dvs-eval-marks-input ${validationErrors[q.attemptId] ? "dvs-eval-marks-input--error" : ""}`}
                        />
                        {validationErrors[q.attemptId] && (
                          <div
                            style={{
                              color: "var(--status-danger)",
                              fontSize: "0.72em",
                              marginTop: "3px",
                            }}
                          >
                            {validationErrors[q.attemptId]}
                          </div>
                        )}
                      </td>
                      <td style={tdStyle}>
                        <input
                          type="text"
                          value={q.remarks ?? ""}
                          onChange={(e) =>
                            onMarksChange(q.attemptId, "remarks", e.target.value)
                          }
                          onBlur={(e) =>
                            handleBlur(q.attemptId, "remarks", e.target.value)
                          }
                          placeholder="—"
                          className="dvs-eval-remarks-input"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ))}
      </div>

      {/* Footer buttons */}
      <div className="dvs-eval-footer">
        <button
          className="dvs-eval-btn dvs-eval-btn--report"
          onClick={onReportIssue}
          disabled={submitting}
        >
          Report Issue
        </button>
        <button
          className="dvs-eval-btn dvs-eval-btn--submit"
          onClick={onSubmit}
          disabled={submitting}
        >
          {submitting ? "Submitting…" : "Submit Evaluation"}
        </button>
      </div>

      {/* Instructions — match production page footer notes */}
      <div className="dvs-eval-instructions">
        <div className="dvs-eval-instructions__marker">NOTE</div>
        <div>A&gt; Each question marks should have to enter.</div>
        <div>B&gt; Obtained marks should not be greater then maximum mark of question.</div>
      </div>
    </div>
  );
};

const thStyle: React.CSSProperties = {
  padding: "4px 6px",
  borderBottom: "1px solid var(--border-default)",
  textAlign: "left",
  fontWeight: 600,
};

const tdStyle: React.CSSProperties = {
  padding: "4px 6px",
  borderBottom: "1px solid var(--border-subtle)",
  verticalAlign: "top",
};

export default MarksEntryPanel;
